import { useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { DownloadIcon } from 'lucide-react'
import { useCurrentShow } from '@/context/current-show'
import { SelectShowNotice } from '@/components/current-show'
import { useShowStaff } from '@/api/hooks'
import { ReportLayout } from '@/layouts/ReportLayout'
import { Button } from '@/components/ui/button'
import { triggerReportPrint } from '@/lib/print'
import { countStaffByRole, groupStaffAssignments } from '@/lib/staff-roster'
import { STAFF_ROLE_LABELS, STAFF_ROLE_ORDER } from '@/data/staff-roles'
import { parseDateOnlyToLocal } from '@/lib/date'

export default function StaffRosterReportPage() {
  const { currentShow } = useCurrentShow()
  const [searchParams] = useSearchParams()

  const showIdParam = searchParams.get('showId') ?? undefined
  const showNameParam = searchParams.get('showName') ?? undefined
  const showLocationParam = searchParams.get('showLocation') ?? undefined
  const showStartDateParam = searchParams.get('showStartDate') ?? undefined
  const showEndDateParam = searchParams.get('showEndDate') ?? undefined

  const showId = currentShow?.id ?? showIdParam
  const showName = currentShow?.name ?? showNameParam ?? 'Selected Show'
  const showLocation = currentShow?.location ?? showLocationParam ?? undefined
  const showStartDate = currentShow?.startDate ?? showStartDateParam ?? undefined
  const showEndDate = currentShow?.endDate ?? showEndDateParam ?? undefined

  const staffQuery = useShowStaff(showId)

  const assignments = staffQuery.data ?? []
  const groups = useMemo(() => groupStaffAssignments(assignments), [assignments])
  const counts = useMemo(() => countStaffByRole(assignments), [assignments])
  const totalStaff = assignments.length

  const preparedRange = useMemo(() => {
    if (!showStartDate || !showEndDate) return undefined
    const start = parseDateOnlyToLocal(showStartDate)
    const end = parseDateOnlyToLocal(showEndDate)
    if (!start || !end) return undefined
    const formatter = new Intl.DateTimeFormat(undefined, { dateStyle: 'medium' })
    return `${formatter.format(start)} – ${formatter.format(end)}`
  }, [showEndDate, showStartDate])

  if (!showId) {
    return <SelectShowNotice description="Choose a show to print the staff roster." />
  }

  const header = (
    <div className="space-y-1">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="space-y-1">
          <p className="text-sm uppercase tracking-wide text-muted-foreground">Staff Roster</p>
          <h1 className="text-3xl font-semibold leading-tight">{showName}</h1>
          {showLocation ? <p className="text-muted-foreground text-sm">{showLocation}</p> : null}
        </div>
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="print-hidden"
          onClick={triggerReportPrint}
        >
          <DownloadIcon className="mr-2 h-4 w-4" aria-hidden="true" />
          Download PDF
        </Button>
      </div>
      {preparedRange ? <p className="text-sm text-muted-foreground">{preparedRange}</p> : null}
    </div>
  )

  if (staffQuery.isLoading) {
    return (
      <ReportLayout header={header}>
        <p className="text-muted-foreground text-base">Loading staff roster…</p>
      </ReportLayout>
    )
  }

  if (staffQuery.isError) {
    return (
      <ReportLayout header={header}>
        <p className="text-destructive text-base">
          {staffQuery.error?.message ?? 'Unable to load staff roster.'}
        </p>
      </ReportLayout>
    )
  }

  if (totalStaff === 0) {
    return (
      <ReportLayout header={header}>
        <p className="text-muted-foreground text-base">No staff assignments have been recorded.</p>
      </ReportLayout>
    )
  }

  return (
    <ReportLayout header={header}>
      <section className="space-y-3">
        <h2 className="text-xl font-semibold">Staff Summary</h2>
        <dl className="grid gap-3 sm:grid-cols-2 md:grid-cols-3 text-base">
          <div className="flex flex-col">
            <dt className="text-sm font-medium text-muted-foreground">Total Staff</dt>
            <dd className="text-lg font-semibold">{totalStaff}</dd>
          </div>
          {STAFF_ROLE_ORDER.map(role => (
            <div key={role} className="flex flex-col">
              <dt className="text-sm font-medium text-muted-foreground">
                {STAFF_ROLE_LABELS[role].plural}
              </dt>
              <dd className="text-lg font-semibold">{counts[role] ?? 0}</dd>
            </div>
          ))}
        </dl>
      </section>

      <section className="space-y-4">
        <h2 className="text-xl font-semibold report-layout__section-heading">Roster Details</h2>
        <div className="space-y-5">
          {groups.map(group => (
            <article key={group.role} className="space-y-2 break-inside-avoid">
              <header className="flex items-baseline justify-between gap-2">
                <h3 className="text-lg font-semibold report-layout__subheading">{group.label}</h3>
                <span className="text-sm text-muted-foreground">
                  {group.members.length}{' '}
                  {group.members.length === 1
                    ? STAFF_ROLE_LABELS[group.role].singular.toLowerCase()
                    : STAFF_ROLE_LABELS[group.role].plural.toLowerCase()}
                </span>
              </header>
              <ul className="space-y-2 text-sm leading-relaxed">
                {group.members.map(member => (
                  <li key={member.id} className="space-y-0.5">
                    <div className="font-medium">{member.name}</div>
                    {member.contact ? (
                      <div className="text-muted-foreground">{member.contact}</div>
                    ) : null}
                  </li>
                ))}
              </ul>
            </article>
          ))}
        </div>
      </section>

      <p className="text-sm text-muted-foreground">
        Prepared for {showName}
        {preparedRange ? ` • ${preparedRange}` : ''}
      </p>
    </ReportLayout>
  )
}
